import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b-2 border-foreground-900/8">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-foreground-950 hover:text-primary-500 transition-colors duration-200">
            <span className="font-heading text-xl font-bold tracking-tight whitespace-nowrap">VigorBuy</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${isHome ? 'text-primary-500' : 'text-foreground-800 hover:text-foreground-950'}`}>
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[520px] bg-white rounded-xl border border-foreground-900/10 shadow-lg py-3 px-2">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 group"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-150 truncate whitespace-nowrap">
                            {cat.name}
                          </p>
                          <p className="text-[11px] text-foreground-500 whitespace-nowrap">{cat.sublabel}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/vigorbuy-sheet-guide" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Shopping Guide
            </Link>
            <a href="#faq" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              FAQ
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold border-2 border-foreground-300 rounded-full text-foreground-800 hover:bg-foreground-50 hover:border-foreground-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              VigorBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-bold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-foreground-900/8 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Primary Navigation */}
            <div className="mb-3 pb-3 border-b border-foreground-900/8">
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-widest mb-2 px-1">Navigation</p>
              <Link to="/" className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold transition-colors ${isHome ? 'bg-primary-50 text-primary-600' : 'text-foreground-800 hover:bg-background-100'}`} onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-home-4-line text-lg"></i>
                Home
              </Link>
              <Link to="/vigorbuy-sheet-guide" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-book-open-line text-lg"></i>
                Shopping Guide
              </Link>
              <a href="#faq" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-question-line text-lg"></i>
                FAQ
              </a>
            </div>

            {/* Categories Section */}
            <div className="mb-3">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="mt-1 ml-2 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs font-medium truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="pt-3 border-t border-foreground-900/8 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 text-sm font-semibold border-2 border-foreground-300 rounded-full text-foreground-800 whitespace-nowrap cursor-pointer hover:bg-foreground-50 transition-colors"
              >
                VigorBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 text-sm font-bold bg-primary-500 text-white rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}